import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api-client.js";
import { EmptyState } from "@/components/ui/EmptyState.js";

interface Customer {
  id: string;
  name: string;
  contactEmail: string | null;
  contactPhone: string | null;
  address: string | null;
  type: string;
  createdAt: string;
}

const TYPE_LABELS: Record<string, string> = {
  private: "Privat", commercial: "Gewerbe", industrial: "Industrie", agricultural: "Landwirtschaft",
};

export function CustomersPage() {
  const [items, setItems] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", contactEmail: "", contactPhone: "", address: "", type: "private" });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const res = await apiClient.get<{ items: Customer[]; total: number }>("/customers");
    if (res.success) setItems(res.data.items);
    setLoading(false);
  };

  useEffect(() => { void load(); }, []);

  const set = (field: keyof typeof form, value: string) => setForm(f => ({ ...f, [field]: value }));

  const handleCreate = async () => {
    setCreating(true); setError(null);
    const res = await apiClient.post<Customer>("/customers", {
      name: form.name.trim(), type: form.type,
      ...(form.contactEmail ? { contactEmail: form.contactEmail } : {}),
      ...(form.contactPhone ? { contactPhone: form.contactPhone } : {}),
      ...(form.address ? { address: form.address } : {}),
    });
    if (res.success) {
      setShowForm(false);
      setForm({ name: "", contactEmail: "", contactPhone: "", address: "", type: "private" });
      void load();
    } else { setError((res as { success: false; error: { message: string } }).error.message); }
    setCreating(false);
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Kunden</h1>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Abbrechen" : "+ Neuer Kunde"}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Neuer Kunde</h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div><label htmlFor="customer-name">Name *</label><input id="customer-name" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Mustermann GmbH" /></div>
            <div><label htmlFor="customer-type">Typ</label><select id="customer-type" value={form.type} onChange={e => set("type", e.target.value)}>{Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><label htmlFor="customer-email">E-Mail</label><input id="customer-email" type="email" value={form.contactEmail} onChange={e => set("contactEmail", e.target.value)} /></div>
            <div><label htmlFor="customer-phone">Telefon</label><input id="customer-phone" value={form.contactPhone} onChange={e => set("contactPhone", e.target.value)} /></div>
            <div style={{ gridColumn: "1 / -1" }}><label htmlFor="customer-address">Adresse</label><input id="customer-address" value={form.address} onChange={e => set("address", e.target.value)} placeholder="Musterstraße 1, 6020 Innsbruck" /></div>
          </div>
          {error && <div style={{ color: "var(--color-error)", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-secondary" onClick={() => setShowForm(false)}>Abbrechen</button>
            <button className="btn-primary" onClick={() => void handleCreate()} disabled={creating || !form.name.trim()}>
              {creating ? "Erstelle…" : "Kunde anlegen"}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ textAlign: "center", padding: 48, color: "var(--color-text-muted)" }}>Lade Kunden…</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon="customers"
            title="Noch keine Kunden vorhanden"
            description="Lege deinen ersten Kunden an, um loszulegen."
            action={<button className="btn-primary" onClick={() => setShowForm(true)}>Ersten Kunden anlegen</button>}
          />
        ) : (
          <>
            <div style={{ marginBottom: 12, fontSize: 13, color: "var(--color-text-muted)" }}>{items.length} Kunde{items.length !== 1 ? "n" : ""}</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                {["Name", "Typ", "E-Mail", "Telefon", "Adresse", "Angelegt"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 12, color: "var(--color-text-muted)", fontWeight: 600 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {items.map((c, i) => (
                  <tr key={c.id} style={{ borderBottom: "1px solid var(--color-border)", background: i % 2 === 0 ? "transparent" : "#fafafa" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 500 }}>
                      <Link to={`/customers/${c.id}`} style={{ color: "var(--color-primary)", textDecoration: "none" }}>{c.name}</Link>
                    </td>
                    <td style={{ padding: "10px 12px" }}><span className="badge badge-draft">{TYPE_LABELS[c.type] ?? c.type}</span></td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--color-text-muted)" }}>{c.contactEmail ?? "–"}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--color-text-muted)" }}>{c.contactPhone ?? "–"}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--color-text-muted)" }}>{c.address ?? "–"}</td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: "var(--color-text-muted)" }}>{new Date(c.createdAt).toLocaleDateString("de-AT")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
